import type React from 'react';
import { useRef, useMemo } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { Effect } from 'postprocessing';
import * as THREE from 'three';

// Logo outline points (scaled from SVG to ~0-23 coordinate range)
// Triangle top + left parallelogram + right parallelogram
const LOGO_OUTLINE_POINTS: [number, number][] = [
  // Triangle (top): (50,15)→(65,45)→(35,45)→back
  [11.50, 3.45], [11.93, 4.31], [12.36, 5.18], [12.79, 6.04], [13.23, 6.90],
  [13.66, 7.76], [14.09, 8.63], [14.52, 9.49], [14.95, 10.35], [14.09, 10.35],
  [13.23, 10.35], [12.36, 10.35], [11.50, 10.35], [10.64, 10.35], [9.78, 10.35],
  [8.91, 10.35], [8.05, 10.35], [8.48, 9.49], [8.91, 8.63], [9.34, 7.76],
  [9.78, 6.90], [10.21, 6.04], [10.64, 5.18], [11.07, 4.31],
  // Left parallelogram: (31,53)→(46,53)→(46,85)→(15,85)→back
  [7.13, 12.19], [7.99, 12.19], [8.86, 12.19], [9.72, 12.19], [10.58, 12.19],
  [10.58, 13.11], [10.58, 14.03], [10.58, 14.95], [10.58, 15.87], [10.58, 16.79],
  [10.58, 17.71], [10.58, 18.63], [10.58, 19.55], [9.69, 19.55], [8.80, 19.55],
  [7.91, 19.55], [7.02, 19.55], [6.12, 19.55], [5.23, 19.55], [4.34, 19.55],
  [3.45, 19.55], [3.91, 18.63], [4.37, 17.71], [4.83, 16.79], [5.29, 15.87],
  [5.75, 14.95], [6.21, 14.03], [6.67, 13.11],
  // Right parallelogram: (54,53)→(69,53)→(85,85)→(54,85)→back
  [12.42, 12.19], [13.28, 12.19], [14.15, 12.19], [15.01, 12.19], [15.87, 12.19],
  [16.33, 13.11], [16.79, 14.03], [17.25, 14.95], [17.71, 15.87], [18.17, 16.79],
  [18.63, 17.71], [19.09, 18.63], [19.55, 19.55], [18.66, 19.55], [17.77, 19.55],
  [16.88, 19.55], [15.99, 19.55], [15.09, 19.55], [14.20, 19.55], [13.31, 19.55],
  [12.42, 19.55], [12.42, 18.63], [12.42, 17.71], [12.42, 16.79], [12.42, 15.87],
  [12.42, 14.95], [12.42, 14.03], [12.42, 13.11],
];

// ============================================
// SHADER-BASED INSTANCED LOGO SYSTEM
// ============================================

// Simplex noise function for organic movement (runs on GPU)
const noiseGLSL = `
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = mod289(i);
    vec4 p = permute(permute(permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
`;

// Vertex shader for instanced logos
const logoVertexShader = `
  ${noiseGLSL}

  // Per-particle attributes
  attribute float pathPosition;
  attribute float particleRandom;
  attribute float particleSize;

  // Per-instance attributes (via instancing)
  attribute vec3 instancePosition;
  attribute float instanceScale;
  attribute vec3 instanceColor;
  attribute float instanceSeed;
  attribute vec3 instanceRotation;

  uniform float uTime;

  varying vec3 vColor;
  varying float vAlpha;
  varying float vGlow;

  mat3 rotationMatrix(vec3 angles) {
    float cx = cos(angles.x);
    float sx = sin(angles.x);
    float cy = cos(angles.y);
    float sy = sin(angles.y);
    float cz = cos(angles.z);
    float sz = sin(angles.z);

    mat3 rx = mat3(
      1.0, 0.0, 0.0,
      0.0, cx, -sx,
      0.0, sx, cx
    );

    mat3 ry = mat3(
      cy, 0.0, sy,
      0.0, 1.0, 0.0,
      -sy, 0.0, cy
    );

    mat3 rz = mat3(
      cz, -sz, 0.0,
      sz, cz, 0.0,
      0.0, 0.0, 1.0
    );

    return rz * ry * rx;
  }

  void main() {
    vec3 localPos = position;

    float flowSpeed = 0.3 + particleRandom * 0.2;
    float flowOffset = sin(uTime * flowSpeed + instanceSeed * 10.0 + pathPosition * 6.28) * 0.02;

    float noiseScale = 2.0;
    float noiseSpeed = 0.5;
    vec3 noisePos = localPos * noiseScale + vec3(instanceSeed * 100.0, uTime * noiseSpeed, 0.0);

    float noiseX = snoise(noisePos) * 0.03;
    float noiseY = snoise(noisePos + vec3(100.0, 0.0, 0.0)) * 0.03;
    float noiseZ = snoise(noisePos + vec3(0.0, 100.0, 0.0)) * 0.02;

    float breathe = sin(uTime * 0.8 + instanceSeed * 5.0) * 0.05 + 1.0;

    vec3 animatedPos = localPos;
    animatedPos.x += noiseX + flowOffset;
    animatedPos.y += noiseY;
    animatedPos.z += noiseZ;
    animatedPos *= breathe;

    float rotSpeed = 0.015 + instanceSeed * 0.01;
    vec3 animatedRotation = instanceRotation + vec3(
      sin(uTime * rotSpeed * 0.5 + instanceSeed * 3.0) * 0.08,
      uTime * rotSpeed * 0.2,
      cos(uTime * rotSpeed * 0.4 + instanceSeed * 2.0) * 0.05
    );
    mat3 rotMat = rotationMatrix(animatedRotation);
    animatedPos = rotMat * animatedPos;

    vec3 worldPos = animatedPos * instanceScale + instancePosition;

    float floatX = sin(uTime * 0.15 + instanceSeed * 2.0) * 0.1;
    float floatY = cos(uTime * 0.12 + instanceSeed * 1.5) * 0.08;
    float floatZ = sin(uTime * 0.1 + instanceSeed) * 0.05;
    worldPos += vec3(floatX, floatY, floatZ);

    vec4 mvPosition = modelViewMatrix * vec4(worldPos, 1.0);

    float distanceScale = 200.0 / -mvPosition.z;
    gl_PointSize = particleSize * distanceScale * instanceScale * breathe;

    gl_Position = projectionMatrix * mvPosition;

    vec3 baseColor = instanceColor;

    float phase = uTime * 0.05 + instanceSeed * 6.28;

    vec3 color1 = vec3(0.3, 0.6, 1.0);
    vec3 color2 = vec3(0.6, 0.3, 0.9);
    vec3 color3 = vec3(0.2, 0.8, 0.7);
    vec3 color4 = vec3(0.9, 0.5, 0.3);

    float t = mod(phase, 4.0);
    vec3 targetColor;
    if (t < 1.0) {
      targetColor = mix(color1, color2, t);
    } else if (t < 2.0) {
      targetColor = mix(color2, color3, t - 1.0);
    } else if (t < 3.0) {
      targetColor = mix(color3, color4, t - 2.0);
    } else {
      targetColor = mix(color4, color1, t - 3.0);
    }

    vColor = mix(baseColor, targetColor, 0.6);

    float twinkle = snoise(vec3(particleRandom * 100.0, uTime * 2.0, instanceSeed)) * 0.3 + 0.7;
    vAlpha = twinkle;

    float nebulaGlow = sin(worldPos.y * 0.15 + uTime * 0.1) * 0.2 + 0.5;
    vGlow = nebulaGlow + sin(uTime * 1.5 + particleRandom * 6.28) * 0.2;
  }
`;

const logoFragmentShader = `
  varying vec3 vColor;
  varying float vAlpha;
  varying float vGlow;

  void main() {
    vec2 center = gl_PointCoord - 0.5;
    float dist = length(center);

    if (dist > 0.5) discard;

    float coreBrightness = 1.0 - smoothstep(0.0, 0.15, dist);
    float glow = exp(-dist * dist * 12.0);
    float outerGlow = exp(-dist * dist * 4.0) * 0.3;

    float alpha = (coreBrightness + glow * vGlow + outerGlow) * vAlpha;

    vec3 finalColor = vColor * (1.0 + coreBrightness * 0.8);
    finalColor += vec3(0.1, 0.15, 0.2) * outerGlow;

    gl_FragColor = vec4(finalColor, alpha * 0.9);
  }
`;

// GPU-accelerated instanced logo renderer
const ShaderLogos = ({
  logos
}: {
  logos: { pos: THREE.Vector3; scale: number; color: THREE.Color }[]
}) => {
  const meshRef = useRef<THREE.Points>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);

  const particlesPerLogo = 50;
  const totalParticles = logos.length * particlesPerLogo;

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();

    const positions = new Float32Array(totalParticles * 3);
    const pathPositions = new Float32Array(totalParticles);
    const particleRandoms = new Float32Array(totalParticles);
    const particleSizes = new Float32Array(totalParticles);

    const instancePositions = new Float32Array(totalParticles * 3);
    const instanceScales = new Float32Array(totalParticles);
    const instanceColors = new Float32Array(totalParticles * 3);
    const instanceSeeds = new Float32Array(totalParticles);
    const instanceRotations = new Float32Array(totalParticles * 3);

    const logoScale = 0.08;
    const offsetX = -23 * logoScale / 2;
    const offsetY = 25 * logoScale / 2;
    const numOutlinePoints = LOGO_OUTLINE_POINTS.length;

    for (let logoIdx = 0; logoIdx < logos.length; logoIdx++) {
      const logo = logos[logoIdx];

      const rotX = (Math.random() - 0.5) * Math.PI * 0.6;
      const rotY = Math.random() * Math.PI * 2;
      const rotZ = (Math.random() - 0.5) * Math.PI * 0.3;
      const logoSeed = logoIdx * 0.1 + Math.random() * 0.05;

      for (let p = 0; p < particlesPerLogo; p++) {
        const i = logoIdx * particlesPerLogo + p;

        const pathT = p / particlesPerLogo;
        const outlineIdx = Math.floor(pathT * numOutlinePoints) % numOutlinePoints;
        const nextIdx = (outlineIdx + 1) % numOutlinePoints;
        const lerpT = (pathT * numOutlinePoints) % 1;

        const [px1, py1] = LOGO_OUTLINE_POINTS[outlineIdx];
        const [px2, py2] = LOGO_OUTLINE_POINTS[nextIdx];
        const px = px1 + (px2 - px1) * lerpT;
        const py = py1 + (py2 - py1) * lerpT;

        const spread = 1.5;
        const spreadX = (Math.random() - 0.5) * spread;
        const spreadY = (Math.random() - 0.5) * spread;
        const spreadZ = (Math.random() - 0.5) * 0.5;

        positions[i * 3] = (px + spreadX) * logoScale + offsetX;
        positions[i * 3 + 1] = -(py + spreadY) * logoScale + offsetY;
        positions[i * 3 + 2] = spreadZ * logoScale;

        pathPositions[i] = pathT;
        particleRandoms[i] = Math.random();
        particleSizes[i] = 2.0 + Math.random() * 3.0;

        instancePositions[i * 3] = logo.pos.x;
        instancePositions[i * 3 + 1] = logo.pos.y;
        instancePositions[i * 3 + 2] = logo.pos.z;
        instanceScales[i] = logo.scale;
        instanceColors[i * 3] = logo.color.r;
        instanceColors[i * 3 + 1] = logo.color.g;
        instanceColors[i * 3 + 2] = logo.color.b;
        instanceSeeds[i] = logoSeed;
        instanceRotations[i * 3] = rotX;
        instanceRotations[i * 3 + 1] = rotY;
        instanceRotations[i * 3 + 2] = rotZ;
      }
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('pathPosition', new THREE.BufferAttribute(pathPositions, 1));
    geo.setAttribute('particleRandom', new THREE.BufferAttribute(particleRandoms, 1));
    geo.setAttribute('particleSize', new THREE.BufferAttribute(particleSizes, 1));
    geo.setAttribute('instancePosition', new THREE.BufferAttribute(instancePositions, 3));
    geo.setAttribute('instanceScale', new THREE.BufferAttribute(instanceScales, 1));
    geo.setAttribute('instanceColor', new THREE.BufferAttribute(instanceColors, 3));
    geo.setAttribute('instanceSeed', new THREE.BufferAttribute(instanceSeeds, 1));
    geo.setAttribute('instanceRotation', new THREE.BufferAttribute(instanceRotations, 3));

    return geo;
  }, [logos, totalParticles]);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime;
    }
  });

  return (
    <points ref={meshRef} geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={logoVertexShader}
        fragmentShader={logoFragmentShader}
        uniforms={{ uTime: { value: 0 } }}
        transparent
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
};

// Twinkling stars shaders
const starVertexShader = `
  attribute float size;
  attribute float twinkleSpeed;
  attribute float twinkleOffset;

  varying vec3 vColor;
  varying float vTwinkle;

  uniform float uTime;

  void main() {
    vColor = color;

    float twinkle = sin(uTime * twinkleSpeed + twinkleOffset) * 0.5 + 0.5;
    twinkle = 0.4 + twinkle * 0.6;
    vTwinkle = twinkle;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);

    gl_PointSize = size * (300.0 / -mvPosition.z) * twinkle;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const starFragmentShader = `
  varying vec3 vColor;
  varying float vTwinkle;

  void main() {
    vec2 center = gl_PointCoord - 0.5;
    float dist = length(center);

    if (dist > 0.5) discard;

    float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
    alpha *= vTwinkle;

    float core = 1.0 - smoothstep(0.0, 0.15, dist);
    vec3 finalColor = vColor + core * 0.5;

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Shader-based starfield with GPU twinkling
const Starfield = () => {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const count = 30000;

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const siz = new Float32Array(count);
    const speeds = new Float32Array(count);
    const offsets = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const radius = 30 + Math.random() * 70;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      pos[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = radius * Math.cos(phi);

      const starType = Math.random();
      let r, g, b;
      if (starType > 0.97) {
        r = 0.7; g = 0.8; b = 1.0;
      } else if (starType > 0.93) {
        r = 0.85; g = 0.9; b = 1.0;
      } else if (starType > 0.88) {
        r = 0.34; g = 0.62; b = 0.97;
      } else if (starType > 0.85) {
        r = 0.08; g = 0.8; b = 0.45;
      } else if (starType > 0.80) {
        r = 1.0; g = 0.95; b = 0.8;
      } else {
        const brightness = 0.8 + Math.random() * 0.2;
        r = brightness; g = brightness; b = brightness + 0.1;
      }
      col[i * 3] = r;
      col[i * 3 + 1] = g;
      col[i * 3 + 2] = b;

      siz[i] = 1.0 + Math.random() * 3.0;
      speeds[i] = 0.5 + Math.random() * 2.0;
      offsets[i] = Math.random() * Math.PI * 2;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(siz, 1));
    geo.setAttribute('twinkleSpeed', new THREE.BufferAttribute(speeds, 1));
    geo.setAttribute('twinkleOffset', new THREE.BufferAttribute(offsets, 1));

    return geo;
  }, []);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime;
    }
  });

  return (
    <points geometry={geometry}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={starVertexShader}
        fragmentShader={starFragmentShader}
        uniforms={{ uTime: { value: 0 } }}
        transparent
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        vertexColors
      />
    </points>
  );
};

// Shooting stars shaders
const shootingStarVertexShader = `
  attribute float seed;
  attribute float trailIndex;
  attribute float cycleOffset;

  uniform float uTime;

  varying float vAlpha;
  varying float vBrightness;

  float hash(float n) {
    return fract(sin(n) * 43758.5453123);
  }

  void main() {
    float cycleDuration = 8.0 + seed * 7.0;
    float activeTime = 2.5;
    float waitTime = cycleDuration - activeTime;

    float cycleTime = mod(uTime + cycleOffset * cycleDuration, cycleDuration);

    float startX = (hash(seed * 1.1) - 0.5) * 80.0 + 20.0;
    float startY = 25.0 + hash(seed * 2.2) * 25.0;
    float startZ = (hash(seed * 3.3) - 0.5) * 50.0;

    float speed = 1.5 + seed * 1.0;
    float vx = -speed * (0.3 + seed * 0.4) * 60.0;
    float vy = -speed * (0.8 + seed * 0.3) * 60.0;
    float vz = speed * (seed - 0.5) * 0.3 * 60.0;

    float trailDelay = trailIndex * 0.015;
    float effectiveTime = max(0.0, cycleTime - waitTime - trailDelay);

    vec3 pos = vec3(
      startX + vx * effectiveTime,
      startY + vy * effectiveTime,
      startZ + vz * effectiveTime
    );

    float isActive = step(waitTime, cycleTime) * step(cycleTime, waitTime + activeTime + trailIndex * 0.015);

    float trailAlpha = 1.0 - trailIndex / 12.0;
    float fadeIn = smoothstep(waitTime, waitTime + 0.1, cycleTime);
    float fadeOut = 1.0 - smoothstep(waitTime + activeTime - 0.3, waitTime + activeTime, cycleTime);

    vAlpha = trailAlpha * fadeIn * fadeOut * isActive;
    vBrightness = 1.0 - trailIndex / 12.0;

    float size = mix(4.0, 1.5, trailIndex / 12.0);

    if (isActive < 0.5) {
      pos = vec3(10000.0);
    }

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = size * (300.0 / -mvPosition.z);
  }
`;

const shootingStarFragmentShader = `
  varying float vAlpha;
  varying float vBrightness;

  void main() {
    float dist = length(gl_PointCoord - vec2(0.5));
    if (dist > 0.5) discard;

    float alpha = exp(-dist * dist * 8.0) * vAlpha;

    vec3 color = vec3(0.7 + vBrightness * 0.3, 0.85 + vBrightness * 0.15, 1.0);

    gl_FragColor = vec4(color, alpha);
  }
`;

const ShootingStars = () => {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const count = 30;
  const trailLength = 12;
  const totalPoints = count * trailLength;

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();

    const positions = new Float32Array(totalPoints * 3);
    const seeds = new Float32Array(totalPoints);
    const trailIndices = new Float32Array(totalPoints);
    const cycleOffsets = new Float32Array(totalPoints);

    for (let i = 0; i < count; i++) {
      const starSeed = Math.random();
      const cycleOffset = Math.random();

      for (let t = 0; t < trailLength; t++) {
        const idx = i * trailLength + t;
        positions[idx * 3] = 0;
        positions[idx * 3 + 1] = 0;
        positions[idx * 3 + 2] = 0;
        seeds[idx] = starSeed;
        trailIndices[idx] = t;
        cycleOffsets[idx] = cycleOffset;
      }
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('seed', new THREE.BufferAttribute(seeds, 1));
    geo.setAttribute('trailIndex', new THREE.BufferAttribute(trailIndices, 1));
    geo.setAttribute('cycleOffset', new THREE.BufferAttribute(cycleOffsets, 1));

    return geo;
  }, []);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime;
    }
  });

  return (
    <points geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={shootingStarVertexShader}
        fragmentShader={shootingStarFragmentShader}
        uniforms={{ uTime: { value: 0 } }}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

// Floating dust/nebula particles
const CosmicDust = () => {
  const pointsRef = useRef<THREE.Points>(null);
  const count = 10000;

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 40;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 60 - 10;

      const hue = Math.random() > 0.6 ? 0.58 + Math.random() * 0.1 : 0.35 + Math.random() * 0.1;
      const color = new THREE.Color().setHSL(hue, 0.5, 0.4 + Math.random() * 0.3);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, []);

  useFrame((state) => {
    if (!pointsRef.current) return;
    const time = state.clock.elapsedTime;
    pointsRef.current.rotation.y = time * 0.01;
    pointsRef.current.rotation.x = Math.sin(time * 0.02) * 0.1;
  });

  return (
    <points ref={pointsRef} geometry={geometry}>
      <pointsMaterial size={0.04} vertexColors transparent opacity={0.6} blending={THREE.AdditiveBlending} sizeAttenuation />
    </points>
  );
};

// Nebula shaders
const nebulaVertexShader = `
  attribute float size;
  varying vec3 vColor;
  varying float vSize;

  void main() {
    vColor = color;
    vSize = size;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (800.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const nebulaFragmentShader = `
  varying vec3 vColor;
  varying float vSize;

  void main() {
    vec2 center = gl_PointCoord - 0.5;
    float dist = length(center);

    float alpha = exp(-dist * dist * 8.0);
    alpha *= 0.12;

    vec3 finalColor = vColor * (1.0 + (1.0 - dist * 2.0) * 0.3);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Chromatic Pulse post-processing effect
const chromaticPulseFragmentShader = `
  uniform float uTime;

  void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
    vec2 centerOffset = vec2(
      sin(uTime * 0.23) * 0.05,
      cos(uTime * 0.19) * 0.04
    );
    vec2 center = (uv - 0.5) - centerOffset;
    float dist = length(center);
    vec2 dir = normalize(center + 0.0001);

    float pulse1 = sin(uTime * 0.8) * 0.5 + 0.5;
    float pulse2 = sin(uTime * 1.3 + 1.0) * 0.3 + 0.5;
    float pulse3 = sin(uTime * 0.5 + 2.5) * 0.2 + 0.5;
    float pulse = (pulse1 + pulse2 + pulse3) / 3.0;

    float chromaticIntensity = min(dist * dist * (0.006 + pulse * 0.01), 0.02);

    vec2 uvR = clamp(uv + dir * chromaticIntensity, 0.001, 0.999);
    vec2 uvB = clamp(uv - dir * chromaticIntensity, 0.001, 0.999);

    float r = texture2D(inputBuffer, uvR).r;
    float g = inputColor.g;
    float b = texture2D(inputBuffer, uvB).b;

    vec3 color = vec3(r, g, b);

    float tempShift = sin(uTime * 0.3) * 0.03;
    color.r += tempShift * (1.0 - dist * 0.5);
    color.b -= tempShift * (1.0 - dist * 0.5);

    float vignette = 1.0 - dist * dist * (0.2 + pulse * 0.1);
    vignette = max(vignette, 0.7);
    color *= vignette;

    outputColor = vec4(color, 1.0);
  }
`;

class ChromaticPulseEffect extends Effect {
  constructor() {
    super('ChromaticPulseEffect', chromaticPulseFragmentShader, {
      uniforms: new Map<string, THREE.Uniform>([
        ['uTime', new THREE.Uniform(0)],
      ]),
    });
  }

  update(_renderer: THREE.WebGLRenderer, _inputBuffer: THREE.WebGLRenderTarget, deltaTime: number) {
    (this.uniforms.get('uTime') as THREE.Uniform).value += deltaTime;
  }
}

const ChromaticPulse = () => {
  const effect = useMemo(() => new ChromaticPulseEffect(), []);
  return <primitive object={effect} />;
};

// Dark nebula shaders
const darkNebulaVertexShader = `
  attribute float size;
  varying float vAlpha;

  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = size * (400.0 / -mvPosition.z);
    vAlpha = 1.0;
  }
`;

const darkNebulaFragmentShader = `
  varying float vAlpha;

  void main() {
    float dist = length(gl_PointCoord - vec2(0.5));
    if (dist > 0.5) discard;

    float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
    alpha = alpha * alpha * 0.85;

    vec3 darkColor = vec3(0.01, 0.01, 0.02);

    gl_FragColor = vec4(darkColor, alpha * vAlpha);
  }
`;

// Dark nebula component
const DarkNebula = () => {
  const count = 40;

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const siz = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const radius = 20 + Math.random() * 50;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      pos[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * 0.7;
      pos[i * 3 + 2] = radius * Math.cos(phi) - 15;

      siz[i] = 20 + Math.random() * 40;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(siz, 1));
    return geo;
  }, []);

  return (
    <points renderOrder={-1} geometry={geometry}>
      <shaderMaterial
        vertexShader={darkNebulaVertexShader}
        fragmentShader={darkNebulaFragmentShader}
        transparent
        blending={THREE.NormalBlending}
        depthWrite={false}
      />
    </points>
  );
};

// Nebula component
const Nebula = () => {
  const count = 120;

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const siz = new Float32Array(count);

    const nebulaColors = [
      [0.25, 0.15, 0.45],
      [0.15, 0.25, 0.55],
      [0.35, 0.15, 0.35],
      [0.1, 0.2, 0.5],
      [0.2, 0.35, 0.55],
      [0.15, 0.4, 0.35],
    ];

    for (let i = 0; i < count; i++) {
      const radius = 35 + Math.random() * 60;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      pos[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * 0.6;
      pos[i * 3 + 2] = radius * Math.cos(phi) - 25;

      const colorIdx = Math.floor(Math.random() * nebulaColors.length);
      const c = nebulaColors[colorIdx];
      col[i * 3] = c[0];
      col[i * 3 + 1] = c[1];
      col[i * 3 + 2] = c[2];

      siz[i] = 15 + Math.random() * 25;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(siz, 1));
    return geo;
  }, []);

  return (
    <points geometry={geometry}>
      <shaderMaterial
        vertexShader={nebulaVertexShader}
        fragmentShader={nebulaFragmentShader}
        transparent
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        vertexColors
      />
    </points>
  );
};

// Camera controller - smooth 3D orbit around logos
const CameraRig = () => {
  const { camera } = useThree();

  useFrame((state) => {
    const time = state.clock.elapsedTime;

    const orbitSpeed = 0.04;
    const radius = 18;

    const x = Math.sin(time * orbitSpeed) * radius * 0.6;
    const z = Math.cos(time * orbitSpeed) * radius * 0.8 + 5;
    const y = Math.sin(time * orbitSpeed * 2) * 4;

    camera.position.x = x;
    camera.position.y = y;
    camera.position.z = z;

    camera.lookAt(0, 0, -8);
  });

  return null;
};

// Main Multi-Logo Scene (exported for reuse in LandingPage)
export const MultiLogoScene = () => {
  const logoConfigs = useMemo(() => {
    const configs: { pos: THREE.Vector3; scale: number; color: THREE.Color; detailed: boolean }[] = [];
    const colors = [
      new THREE.Color('#7c6cf0'),
      new THREE.Color('#4ac4f0'),
      new THREE.Color('#f07c6c'),
      new THREE.Color('#6cf0b8'),
      new THREE.Color('#f0c44a'),
      new THREE.Color('#c46cf0'),
    ];

    // Generate 60 detailed logos spread everywhere
    for (let i = 0; i < 60; i++) {
      const radius = 2 + Math.pow(Math.random(), 0.5) * 28;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta) * 0.6;
      const z = radius * Math.cos(phi) - 10;

      const scale = 0.5 + Math.random() * 0.8;

      configs.push({
        pos: new THREE.Vector3(x, y, z),
        scale,
        color: colors[Math.floor(Math.random() * colors.length)],
        detailed: true
      });
    }

    // Generate 8000 tiny logos filling the space
    for (let i = 0; i < 8000; i++) {
      const radius = 3 + Math.pow(Math.random(), 0.4) * 40;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta) * 0.5;
      const z = radius * Math.cos(phi) - 12;

      const distanceScale = Math.max(0.1, 1 - radius / 45);
      const scale = 0.06 + Math.random() * 0.2 * distanceScale;

      configs.push({
        pos: new THREE.Vector3(x, y, z),
        scale,
        color: colors[Math.floor(Math.random() * colors.length)],
        detailed: false
      });
    }

    return configs;
  }, []);

  const allLogos = logoConfigs.map(c => ({
    pos: c.pos,
    scale: c.scale,
    color: c.color
  }));

  return (
    <>
      <color attach="background" args={['#010103']} />

      <CameraRig />
      <DarkNebula />
      <Nebula />
      <Starfield />
      <ShootingStars />
      <CosmicDust />
      <ShaderLogos logos={allLogos} />

      <EffectComposer>
        <Bloom
          intensity={1.2}
          luminanceThreshold={0.3}
          luminanceSmoothing={0.9}
          mipmapBlur
        />
        <ChromaticPulse />
      </EffectComposer>
    </>
  );
};

// Reusable cosmic background canvas
// Pass active={false} to pause the WebGL render loop (e.g. when scrolled out of viewport)
export const CosmicBackground = ({ style, active = true }: { style?: React.CSSProperties; active?: boolean }) => (
  <Canvas
    camera={{ position: [0, 0, 8], fov: 60 }}
    style={{ position: 'fixed', inset: 0, zIndex: 0, ...style }}
    gl={{ antialias: true, alpha: true }}
    frameloop={active ? 'always' : 'never'}
  >
    <MultiLogoScene />
  </Canvas>
);

// Main Kickoff Page Component
export const KickoffPage = () => {
  useDocumentTitle('Kickoff – qtplan');
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      background: '#0a0e14',
    }}>
      <Canvas
        camera={{ position: [0, 0, 8], fov: 60 }}
        style={{ position: 'absolute', inset: 0 }}
        gl={{ antialias: true, alpha: true }}
      >
        <MultiLogoScene />
      </Canvas>
    </div>
  );
};

export default KickoffPage;
